import { type FC, useState, useContext } from 'react';
import classnames from 'classnames';
import { graphql, useFragment, useMutation } from 'react-relay';

import { FormattedMessage, defineMessages, useIntl } from 'dibs-react-intl';
import { TextArea } from 'dibs-elements/exports/TextArea.tsx';
import { Spinner } from 'dibs-elements/exports/Spinner.tsx';
import { Button } from 'dibs-elements/exports/Button.tsx';
import { Link } from 'dibs-elements/exports/Link.tsx';

import { GetHelpZendeskContext } from './GetHelpZendeskContext.ts';

import { type GetHelpZendeskContactBuyer_viewer$key } from './__generated__/GetHelpZendeskContactBuyer_viewer.graphql.ts';
import { type GetHelpZendeskContactBuyerMutation } from './__generated__/GetHelpZendeskContactBuyerMutation.graphql.ts';

import dibsCss from 'dibs-css';

const viewerFragment = graphql`
    fragment GetHelpZendeskContactBuyer_viewer on Viewer {
        transaction(transactionId: $transactionId) @include(if: $hasOrderId) {
            ...ContactBuyerForm_transaction @include(if: $hasSellerId)
            conversation {
                serviceId
            }
            item {
                serviceId
            }
            buyer {
                serviceId
            }
        }
    }
`;

const messages = defineMessages({
    placeholder: {
        id: 'getHelp.GetHelpZendeskContactBuyer.textAreaPlaceholder',
        defaultMessage: 'Send a message to the client to request more details.',
    },
    errorMessage: {
        id: 'getHelp.GetHelpZendeskContactBuyer.errorMessage',
        defaultMessage: 'An error occurred while sending the message.',
    },
});

const GetHelpZendeskContactBuyer: FC<{ viewer: GetHelpZendeskContactBuyer_viewer$key }> = ({
    viewer: viewerRef,
}) => {
    const intl = useIntl();
    const {
        state: { sellerId },
    } = useContext(GetHelpZendeskContext);

    const viewer = useFragment(viewerFragment, viewerRef);
    const [contactBuyer, isLoading] = useMutation<GetHelpZendeskContactBuyerMutation>(graphql`
        mutation GetHelpZendeskContactBuyerMutation($input: SaveMessageInput!) {
            saveMessage(input: $input) {
                conversation {
                    serviceId
                }
            }
        }
    `);

    const { transaction } = viewer || {};
    const { conversation, item, buyer } = transaction || {};
    const itemId = item?.serviceId;
    const buyerId = buyer?.serviceId;

    const [isFormOpen, setIsFormOpen] = useState(false);
    const [messageContent, setMessageContent] = useState('');
    const [showSuccessMessage, setShowSuccessMessage] = useState(false);
    const [errorMessage, setErrorMessage] = useState('');
    const [conversationId, setConversationId] = useState(conversation?.serviceId);

    const conversationLink = `/dealers/conversations/${conversationId}`;

    const sendMessage = (): void => {
        setErrorMessage('');
        contactBuyer({
            variables: {
                input: {
                    textBody: messageContent,
                    senderId: sellerId,
                    isFromSeller: true,
                    itemId,
                    recipientId: buyerId,
                },
            },
            onCompleted(response) {
                setShowSuccessMessage(true);
                setConversationId(response.saveMessage?.conversation?.serviceId || '');
            },
            onError(error) {
                setErrorMessage(error?.message || intl.formatMessage(messages.errorMessage));
            },
        });
    };

    if (showSuccessMessage) {
        return (
            <div className={dibsCss.sassyFontBody}>
                <span className={classnames(dibsCss.block, dibsCss.mbXsmall)}>
                    <FormattedMessage
                        id="getHelp.GetHelpZendeskContactBuyer.successTitle"
                        defaultMessage="Your message was sent!"
                    />
                </span>
                <FormattedMessage
                    id="getHelp.GetHelpZendeskContactBuyer.successInfo"
                    defaultMessage="We'll send you an email when the client responds. View all your messages at any time in <a>Inbox</a>."
                    values={{
                        a: content => (
                            <Link target="_blank" href={`/dealers/conversations/${conversationId}`}>
                                <span className={dibsCss.sassyFontBody}>{content}</span>
                            </Link>
                        ),
                    }}
                />
            </div>
        );
    }

    return (
        <div>
            {!isFormOpen && (
                <Button
                    {...(conversationId
                        ? {
                              href: conversationLink,
                              target: '_blank',
                          }
                        : {
                              onClick: () => {
                                  setIsFormOpen(true);
                              },
                          })}
                    size="medium"
                >
                    <FormattedMessage
                        id="getHelp.GetHelpZendeskContactBuyer.contactBuyer"
                        defaultMessage="Send Client a Message"
                    />
                </Button>
            )}
            {isFormOpen && (
                <>
                    <TextArea
                        dataTn="get-help-modal-contact-buyer-textarea"
                        onChange={e => setMessageContent(e.target.value)}
                        placeholder={intl.formatMessage(messages.placeholder)}
                        rows={3}
                        value={messageContent}
                    />
                    <Button
                        onClick={() => {
                            sendMessage();
                        }}
                        size="medium"
                        disabled={!messageContent || isLoading}
                        className={dibsCss.mtSmall}
                    >
                        <span
                            className={classnames(
                                dibsCss.minW200px,
                                dibsCss.flex,
                                dibsCss.justifyCenter
                            )}
                        >
                            {isLoading ? (
                                <Spinner
                                    size="tiny"
                                    containerClass={classnames(
                                        dibsCss.flex,
                                        dibsCss.justifyCenter,
                                        dibsCss.itemsCenter
                                    )}
                                />
                            ) : (
                                <FormattedMessage
                                    id="getHelp.GetHelpZendeskContactBuyer.send"
                                    defaultMessage="Send"
                                />
                            )}
                        </span>
                    </Button>
                    {!!errorMessage && (
                        <div
                            className={classnames(
                                dibsCss.sassyFontBody,
                                dibsCss.textAlertprimary,
                                dibsCss.mtXsmall
                            )}
                        >
                            {errorMessage}
                        </div>
                    )}
                </>
            )}
        </div>
    );
};

export { GetHelpZendeskContactBuyer };
